// Classic's header account control (registration plan step 2). Rendered by
// Navigation only when core's `nav.account` is set (the registration add-on
// is on), so a league without it gets no element, class, state or effect:
// the hooks below live in components that are not mounted then.
//
//   desktop  signed out: a "Sign in" link styled like the nav links;
//            signed in: the first name + chevron opening a small menu
//            styled like the More dropdown (Signed in as …, Sign out).
//   drawer   an account block above the links (44 px targets).

import { Link } from 'react-router-dom'
import type { SiteAccountNav } from '@/site/core/chrome/useSiteAccount'
import { sf } from '@/site/core/features/sf'
import { useLabels } from '@/site/core/labels'
import { useAccountMenu } from '@/site/kit/account/useAccountMenu'

type SignedIn = Extract<SiteAccountNav, { state: 'signedIn' }>

function SignedInMenu({ account }: { account: SignedIn }) {
  const ui = useLabels().ui.account
  const { open, setOpen, toggle, triggerRef, panelRef, panelId } = useAccountMenu()
  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={account.menuLabel}
        className={`flex items-center gap-1 px-4 py-2 min-h-[44px] text-sm font-bold uppercase tracking-wider transition-all ${
          open ? 'text-primary' : 'text-white/80 hover:text-white'
        }`}
        {...sf('site.nav.account')}
      >
        <span className="max-w-[10rem] truncate">{account.label}</span>
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          className="absolute right-0 top-full mt-2 min-w-[14rem] bg-bg-secondary border border-secondary/30 rounded-lg shadow-2xl py-2"
        >
          {account.signedInAs && <p className="px-4 py-2 text-xs text-white/50 truncate">{account.signedInAs}</p>}
          {account.accountHref && (
            <Link
              to={account.accountHref}
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm font-bold uppercase tracking-wider text-white/80 hover:text-white hover:bg-white/5"
            >
              {ui.myAccount}
            </Link>
          )}
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              account.signOut()
            }}
            className="block w-full text-left px-4 py-2 min-h-[44px] text-sm font-bold uppercase tracking-wider text-white/80 hover:text-white hover:bg-white/5"
          >
            {ui.signOut}
          </button>
        </div>
      )}
    </div>
  )
}

/** The desktop control, inside Navigation's measured switcher wrapper. */
export function AccountMenu({ account }: { account: SiteAccountNav }) {
  if (account.state === 'signedIn') return <SignedInMenu account={account} />
  return (
    <Link
      to={account.signInHref}
      aria-current={account.active ? 'page' : undefined}
      className={`px-4 py-2 text-sm font-bold uppercase tracking-wider whitespace-nowrap transition-all ${
        account.active ? 'text-primary' : 'text-white/80 hover:text-white'
      }`}
      {...sf('site.nav.account')}
    >
      {account.label}
    </Link>
  )
}

/** The drawer's account block (above the links). */
export function MobileAccount({ account }: { account: SiteAccountNav }) {
  const ui = useLabels().ui.account
  if (account.state === 'signedOut') {
    return (
      <div className="mb-4">
        <Link
          to={account.signInHref}
          aria-current={account.active ? 'page' : undefined}
          className={`block px-4 py-3 text-base font-bold uppercase tracking-wider border-l-2 transition-all ${
            account.active ? 'text-primary border-primary bg-primary/5' : 'text-white/80 border-transparent hover:text-white hover:bg-white/5'
          }`}
          {...sf('site.nav.account')}
        >
          {account.label}
        </Link>
      </div>
    )
  }
  return (
    <div className="mb-4 border-b border-secondary/30 pb-4" {...sf('site.nav.account')}>
      {account.signedInAs && <p className="px-4 pb-1 text-sm text-white/60 truncate">{account.signedInAs}</p>}
      {account.accountHref && (
        <Link to={account.accountHref} className="block px-4 py-3 text-base font-bold uppercase tracking-wider text-white/80 hover:text-white">
          {ui.myAccount}
        </Link>
      )}
      <button
        type="button"
        onClick={account.signOut}
        className="block w-full text-left px-4 py-3 min-h-[44px] text-base font-bold uppercase tracking-wider text-white/80 hover:text-white hover:bg-white/5"
      >
        {ui.signOut}
      </button>
    </div>
  )
}
